// The RequestResponseProtocol implementation for receivers that support
// channel-style messaging.

import { AssociatedType } from '@fullstory/typeutils';
import {
  AnyRequestResponseMessage,
  RequestResponseProtocol,
  RequestType,
  ResponseOrException,
  ResponseOrExceptionFor,
} from '../requestresponse';

// A message receiver. This is the interface exposed by postMessage() in
// basically all situations other than, unfortunately,
// Window.prototype.postMessage().
export interface Messageable {
  postMessage(message: any, transfer?: Transferable[]): void;
}

// Returns a Messageable wrapper around window.postMessage.
export function messageableForWindow(
  wnd: WindowLikeMessageable,
  targetOrigin: string,
): Messageable {
  return {
    postMessage(message: any, transfer?: Transferable[]): void {
      wnd.postMessage(message, targetOrigin, transfer);
    },
  };
}

// Write the relevant part of the `Window` type out here rather
// than depending on all the DOM types.
interface WindowLikeMessageable {
  postMessage(message: any, targetOrigin: string, transfer?: Transferable[]): void;
}

// The subset of MessageEvent properties that are actually used by
// handleMessage(). This allows us to support both MessageEvent and
// ExtendableMessageEvent, which are not assignable with the current typings,
// without resorting to callsite casts.
export interface MessageLikeEvent extends Event {
  readonly data: any;
  readonly ports: ReadonlyArray<MessagePort>;
}

// The request/response protocol implementation for channel-style messaging.
export class MessageRequestResponseProtocol<
  M extends AnyRequestResponseMessage,
> extends RequestResponseProtocol<M> {
  // Channel-style messaging delivers data as a message event.
  _FrameType!: AssociatedType<MessageLikeEvent>;
  // Requests are sent to a Messageable object.
  _RequestReceiverType!: AssociatedType<Messageable>;
  // Responses are sent to an ephemeral MessagePort.
  _ResponseReceiverType!: AssociatedType<MessagePort>;
  // ...And may include things like MessagePorts that need to be _transfered_.
  _MetadataType!: AssociatedType<Transferable[]>;

  protected readRequest(frame: MessageLikeEvent): [RequestType<M>, MessagePort] {
    const port = frame.ports[0];
    if (!port) {
      throw new Error(`No response port when handling request/response message`);
    }
    return [frame.data as RequestType<M>, port];
  }

  protected sendRequest<Request extends RequestType<M>>(
    receiver: Messageable,
    request: Request,
    transferables: Transferable[] = [],
  ): Promise<ResponseOrExceptionFor<M, Request>> {
    return new Promise<ResponseOrExceptionFor<M, Request>>((resolve, reject) => {
      const channel = new MessageChannel();
      const localPort: MessagePort = channel.port1;
      const remotePort = channel.port2;

      localPort.addEventListener('message', (resultMessage: MessageLikeEvent) => {
        localPort.close();
        resolve(resultMessage.data as ResponseOrExceptionFor<M, Request>);
      });
      localPort.addEventListener('messageerror', (error: Event) => {
        localPort.close();
        reject(error);
      });
      localPort.start();

      // Send the message, and include a port that the receiver can use to respond.
      receiver.postMessage(request, [remotePort, ...transferables]);
    });
  }

  protected sendResponse(
    receiver: MessagePort,
    response: ResponseOrException<M>,
    transferables: Transferable[] = [],
  ): void {
    receiver.postMessage(response, transferables);
    receiver.close();
  }
}
